import React, { useCallback, useEffect, useRef, useState } from "react";
import "./MultilineTextWithCopy.scss";

export interface MultilineTextWithCopyProps {
  primaryText: string;
  secondaryText: string;
  copyValue?: string;
  copyIconColor?: string;
  copyIconSize?: number;
  shouldTruncate?: boolean;
  shouldTruncateSecondary?: boolean;
  className?: string;
}

interface CopyIconProps {
  color: string;
  size: number;
}

/**
 * Copy glyph used by {@link MultilineTextWithCopy}.
 *
 * @param props - Icon color and pixel size
 * @returns Copy icon svg
 */
function CopyIcon({ color, size }: CopyIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="7.5"
        y="2.5"
        width="10"
        height="12"
        rx="1.5"
        stroke={color}
        strokeWidth="1.2"
      />
      <path
        d="M12.5 14.5V16C12.5 16.8284 11.8284 17.5 11 17.5H4C3.17157 17.5 2.5 16.8284 2.5 16V7C2.5 6.17157 3.17157 5.5 4 5.5H5.5"
        stroke={color}
        strokeWidth="1.2"
      />
    </svg>
  );
}

/**
 * Two-line text with a copy control, matching the oasis multiline copy cell.
 * The copy button cancels the click before it reaches the table row.
 *
 * @param props - Primary and secondary text plus the value written to the clipboard
 * @returns Two-line text and a copy button
 */
export const MultilineTextWithCopy: React.FC<MultilineTextWithCopyProps> = ({
  primaryText,
  secondaryText,
  copyValue,
  copyIconColor = "#0056D6",
  copyIconSize = 16,
  shouldTruncate = false,
  shouldTruncateSecondary = false,
  className = "",
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const valueToCopy = copyValue ?? secondaryText;
      try {
        await navigator.clipboard.writeText(valueToCopy);
        setShowCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setShowCopied(false), 1500);
      } catch {
        // Clipboard API not available
      }
    },
    [copyValue, secondaryText],
  );

  return (
    <div className={`multi-line-text-with-copy ${className}`}>
      <span
        className={`multi-line-text-with-copy__primary ${
          shouldTruncate ? "multi-line-text-with-copy__primary--truncate" : ""
        }`}
        title={shouldTruncate ? primaryText : undefined}
      >
        {primaryText}
      </span>
      <div className="multi-line-text-with-copy__secondary-row">
        <span
          className={`multi-line-text-with-copy__secondary ${
            shouldTruncateSecondary
              ? "multi-line-text-with-copy__primary--truncate"
              : ""
          }`}
          title={shouldTruncateSecondary ? secondaryText : undefined}
        >
          {secondaryText}
        </span>
        <button
          className="multi-line-text-with-copy__copy-btn"
          onClick={handleCopy}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          type="button"
          aria-label={`Copy ${secondaryText}`}
        >
          <CopyIcon color={copyIconColor} size={copyIconSize} />
        </button>
        {showCopied && (
          <span className="multi-line-text-with-copy__copied">Copied!</span>
        )}
      </div>
    </div>
  );
};

export default MultilineTextWithCopy;
